<script lang="ts" setup>
import slugify from '@sindresorhus/slugify'
import sanitizeHtml from '~/assets/vendors/sanitize-html'
import type { JobItem } from '~/models/Job'
import { useUser } from '~/store/user'
import { useJobs } from '~/store/job'
import browseJobEmployement from '~/assets/data/browsejob-employement.json'
import nonLocLongtail from '~/assets/data/non-loc-longtail.json'

interface Props {
  item: JobItem
  showRight: boolean
}

interface IFrame {
  tag: string
  attribs: { [index: string]: string }
  text: string
  tagPosition: number
}

const props = defineProps<Props>()
const emit = defineEmits(['isWaiting', 'setSchema'])
const route = useRoute()
const config = useRuntimeConfig()
const userStore = useUser()
const jobStore = useJobs()
const modalOpen = ref(false)
const isLoading = ref(!!process.client)
const isShareOpen = ref(false)
const isError = ref()
const repeatedAction = ref(0)
const timer = ref<number>(13)
const simplifiedVer = ref()
const selectedMode = ref('ori')
const jobUrl = computed(() => `/job/${slugify(`${props.item?.job_id}-${props.item?.title}`)}`)
const job = computed(() => props.item as JobItem)
const rightShowing = computed(() => props.showRight)
const redirect = useCookie('redirect', { path: '/', domain: config.public.COOKIE_DOMAIN })
const token = computed(() => {
  if (!userStore.token)
    redirect.value = route.fullPath
  return userStore.token
})

onMounted(async () => {
  isLoading.value = false
  await nextTick()
  if (!isEmpty(token.value) && route.query?.mode === 'simplified')
    useSimplified()
})

const { pause, resume, isActive } = useIntervalFn(() => {
  if (timer.value <= 1)
    pause()
  if (!isActive.value) {
    isLoading.value = false
    if (repeatedAction.value === 2) {
      isError.value = '503'
      useSetTracking(route.path.includes('dashboard') ? 'job_recommendation_result_simplify_failed' : 'job_page_simplify_failed', { job_title: job.value.title, job_location: (job.value.locations[0]?.city ? `${job.value.locations[0]?.city}, ` : '') + (job.value.locations[0]?.country || ''), failed_reason: isError.value })
    }
    else if (!isError.value && !simplifiedVer.value && repeatedAction.value < 2) { emit('isWaiting', isError.value || 'API inprogress') }
  }
  timer.value--
}, 1000, { immediate: false })

function startTimer() {
  isLoading.value = true
  timer.value = 13
  resume()
}

async function useSimplified() {
  useSetTracking(route.path.includes('dashboard') ? 'job_recommendation_result_simplify_click' : 'job_page_simplify_click', { job_title: job.value.title, job_location: (job.value.locations[0]?.city ? `${job.value.locations[0]?.city}, ` : '') + (job.value.locations[0]?.country || '') })
  isError.value = null
  repeatedAction.value = 0
  simplifiedVer.value = null
  selectedMode.value = 'sim'
  if (isEmpty(props.item?.simplified)) {
    if (token.value) {
      startTimer()
      const { data, error } = await useCompanyApi('/talent/job/simplified/desc', {
        method: 'POST',
        body: { jobId: job.value.job_id },
        initialCache: false,
        useAuth: true,
      })
      const resp = data?.value as { success: boolean; message: string; error: string; description: string }
      isLoading.value = false
      if (resp && resp.success) {
        useSetTracking(route.path.includes('dashboard') ? 'job_recommendation_result_simplify_success' : 'job_page_simplify_success', { job_title: job.value.title, job_location: (job.value.locations[0]?.city ? `${job.value.locations[0]?.city}, ` : '') + (job.value.locations[0]?.country || '') })

        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = resp.description
        const sections = tempDiv.querySelectorAll('section')
        const dataArray = Array.from(sections).map((section) => {
          const sectionId = section.getAttribute('id')
          const title = section.querySelector('h2').textContent
          const ulElement = section.querySelector('ul')
          const liElements = ulElement ? ulElement.querySelectorAll('li') : []
          const pElement = section.querySelector('p')
          const content = pElement ? pElement.outerHTML : (liElements.length > 0 ? ulElement.outerHTML : null)
          return { section: sectionId, title, content }
        })
        const index = jobStore.jobs.findIndex((job: JobItem) => job.job_id === props.item?.job_id)
        if (index >= 0)
          jobStore.jobs[index].simplified = dataArray
        simplifiedVer.value = dataArray
      }
      else {
        isError.value = resp?.error || error.value?.statusCode
        useSetTracking(route.path.includes('dashboard') ? 'job_recommendation_result_simplify_failed' : 'job_page_simplify_failed', { job_title: job.value.title, job_location: (job.value.locations[0]?.city ? `${job.value.locations[0]?.city}, ` : '') + (job.value.locations[0]?.country || ''), failed_reason: resp?.error || error.value?.data?.error })
      }
    }
    else {
      selectedMode.value = 'ori'
      modalOpen.value = true
    }
  }
  else if (!isEmpty(props.item?.simplified)) {
    simplifiedVer.value = props.item?.simplified
  }
}

const jobSummary = computed(() => {
  if (job.value?.ver === 2) { return job.value?.jobSummary }
  else {
    if (selectedMode.value === 'sim')
      return !isEmpty(simplifiedVer.value) ? simplifiedVer.value.find((obj: any) => obj.section === 'job-summary')?.content : ''
  }
})

const jobResponsibility = computed(() => {
  if (job.value?.ver === 2) { return job.value?.keyResponsibilities }
  else {
    if (selectedMode.value === 'sim')
      return !isEmpty(simplifiedVer.value) ? simplifiedVer.value.find((obj: any) => obj.section === 'key-responsibilities')?.content : ''
    else
      return formatContent(job.value.responsibilities, 'responsibility')
  }
})

const jobRequirement = computed(() => {
  if (job.value?.ver === 2) { return job.value?.qualifications }
  else {
    if (selectedMode.value === 'sim')
      return !isEmpty(simplifiedVer.value) ? simplifiedVer.value.find((obj: any) => obj.section === 'qualifications-requirements')?.content : ''
    else
      return formatContent(job.value.requirements, 'requirement')
  }
})

const jobBenefit = computed(() => {
  if (job.value?.ver === 2) { return job.value?.compensation }
  else {
    if (selectedMode.value === 'sim')
      return !isEmpty(simplifiedVer.value) ? simplifiedVer.value.find((obj: any) => obj.section === 'compensation-benefits')?.content : ''
    else
      return formatContent(job.value.benefits, 'benefit')
  }
})

function formatContent(rawContent: string, type: string) {
  try {
    const newContent = sanitizeHtml(rawContent, {
      exclusiveFilter(frame: IFrame) {
        if (frame.tag === 'p')
          return !frame.text.trim()
        else if (['a', 'pre'].includes(frame.tag))
          return frame.text.trim()
      },
      textFilter(text: string) {
        let newStr = text
        newStr = newStr.replace('null', '')
        newStr = newStr.replace(/[\s]+/g, ' ')
        return newStr
      },
    })
    if (type === 'responsibility')
      emit('setSchema', JSON.stringify(newContent))
    return newContent
  }
  catch (err) {
    if (type === 'responsibility')
      emit('setSchema', JSON.stringify(rawContent))
    return rawContent
  }
}
</script>

<template>
  <div v-if="job?.id" class="grid grid-cols-12 gap-6">
    <!-- LEFT SIDE -->
    <div :class="`${route.path.includes('/dashboard') ? 'col-span-12' : 'col-span-12 lg:col-span-8'} bg-white pt-6 pb-10 rounded-md shadow-sm auto-rows-min relative`">
      <ContinueFormModal :redirect-val="`${route.fullPath}?mode=simplified`" :open="modalOpen" @open-modal="(item: boolean) => modalOpen = item" />
      <!-- HEADER -->
      <div class="px-4 lg:px-6">
        <!-- LOGO & ACTIONS -->
        <div class="flex mb-4">
          <div class="flex-shrink-0 flex items-center">
            <img v-if="job.company_logo" :src="job.company_logo" class="object-contain w-14 h-14">
            <div v-else class="bg-[#EEF2FF] text-[#4F46E5] text-5xl font-semibold text-center flex items-center w-14 h-14">
              <span class="mx-auto">{{ job.company_name.charAt(0) }}</span>
            </div>
          </div>
          <div class="ml-auto flex">
            <BrowsejobsModalReport :report-redirect="true" :job-id="job.job_id" icon="material-symbols:flag-outline" :with-title="false" />
            <div class="flex-shrink-0 flex items-center cursor-pointer ml-2" @click="isShareOpen = true">
              <Icon name="material-symbols:ios-share" class="w-5 h-5 text-[#5843E5]" />
            </div>
            <BaseMoleculesSharing :url-path="jobUrl" item-type="job" :title="job.title" :open-share="isShareOpen" :popup-only="true" @close-share="isShareOpen = false" />
          </div>
        </div>
        <h1 class="capitalize text-3xl font-bold mb-2">
          {{ job.title }}
        </h1>

        <!-- MINI INFO -->
        <ul class="capitalize text-xs text-gray-600 flex space-x-2 mb-4">
          <li class="border-r-1 pr-2">
            <NuxtLink v-if="nonLocLongtail.includes(browseJobEmployement.find(type => type.value === job.types[0])?.label.toLowerCase().replaceAll(' ', '-'))" :to="useGenerateSearchUrl('', '', job.types[0])" class="text-[#5843E5]">
              {{ browseJobEmployement.find(type => type.value === job.types[0])?.label }}
            </NuxtLink>
            <span v-else>{{ browseJobEmployement.find(type => type.value === job.types[0])?.label }}</span>,

            <NuxtLink v-if="nonLocLongtail.includes(job.remote)" :to="useGenerateSearchUrl('', '', '', job.remote)" class="text-[#5843E5]">
              {{ job.remote }}
            </NuxtLink>
            <span v-else>{{ job.remote }}</span>
          </li>
          <li class="border-r-1 pr-2">
            <NuxtLink v-if="!isEmpty(job.company_slug)" :to="`/companies/${job.company_slug}`" class="mb-1 text-[#5843E5]">
              {{ job.company_name }}
            </NuxtLink>
            <span v-else class="mb-1">{{ job.company_name }}</span>
          </li>
          <li v-if="job.locations">
            {{ job.locations[0]?.city ? `${job.locations[0]?.city}, ` : '' }} {{ job.locations[0]?.country }}
          </li>
        </ul>

        <!-- SALARY -->
        <div class="py-2">
          <div v-if="job?.is_salary_undisclosed || (parseInt(job?.salary_max_converted || job?.salary_max) === 0 && parseInt(job?.salary_min_converted || job?.salary_min) === 0)" class="text-lg font-semibold">
            Salary undisclosed
          </div>
          <div v-else-if="!job?.is_salary_undisclosed && (parseInt(job?.salary_max_converted || job?.salary_max) > 0 || parseInt(job?.salary_min_converted && job?.salary_min) > 0)" class="flex items-center text-lg">
            <span class="mr-1 font-bold">{{ useCurrencyFormat(job?.currency_converted || job?.currency, parseInt(job?.salary_min_converted || job?.salary_min), null, false, false, false) }} - {{ useCurrencyFormat(job?.currency_converted || job?.currency, parseInt(job?.salary_max_converted || job?.salary_max), null, false, false, false) }}</span>
            <span class="text-xs"> / {{ useSalaryTypeFormat(job?.salary_type) }}</span>
          </div>
        </div>
      </div>

      <hr class="border-gray-300 mx-4">

      <!-- JOB APPLY BUTTON -->
      <div class="my-4 px-4 lg:px-6">
        <JobsApplyButton :with-title="true" :user-token="token" :job="job" />
      </div>

      <hr class="border-gray-300 mx-4 mb-4">

      <!-- JOB DESCRIPTION -->
      <div class="px-4 lg:px-6">
        <!-- LOADING -->
        <div v-if="isLoading">
          <div v-for="(i) in 4" :key="i" class="w-full mb-4 animate-pulse">
            <div class="w-[35%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[100%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[100%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[100%] bg-gray-200 h-8 mb-1 rounded-md" />
          </div>
          <div class="w-full mb-4 animate-pulse">
            <div class="w-[35%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[55%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[80%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[55%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[80%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[55%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[80%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[55%] bg-gray-200 h-8 mb-1 rounded-md" />
            <div class="w-[80%] bg-gray-200 h-8 mb-1 rounded-md" />
          </div>
        </div>

        <div v-else-if="!isLoading" class="space-y-8 prose-sm break-words">
          <!-- ORIGINAL SIMPLIFIED BUTTON -->
          <div v-if="job?.ver !== 2" class="flex items-center p-2 space-x-2 bg-gray-50 mx-auto text-xs font-bold" style="width: fit-content;">
            <div :class="`${selectedMode === 'ori' ? 'bg-white text-indigo-500' : ''} flex items-center py-2 pl-2 pr-4 cursor-pointer`" @click="selectedMode = 'ori'">
              <Icon name="ic:baseline-edit-note" class="w-5 h-5 mr-2" /> {{ $t('job_detail_original') }}
            </div>
            <div :class="`${selectedMode === 'sim' ? 'bg-white text-indigo-500' : ''} flex items-center py-2 pl-2 pr-4 cursor-pointer`" @click="useSimplified()">
              <Icon name="ic:baseline-auto-mode" class="w-5 h-5 mr-2" /> {{ $t('job_detail_simplified') }}
            </div>
          </div>

          <div v-if="selectedMode === 'sim'">
            <!-- SHOW MODAL: TAKE LONGER  -->
            <div v-if="!isError && isEmpty(simplifiedVer) && !isActive" class="my-20 p-6 shadow-md rounded-sm w-[368px] text-center space-y-6 text-gray-900 m-auto">
              <Icon name="mdi:av-timer" class="w-12 h-12 text-indigo-500" />
              <p class="text-xl">
                {{ $t('job_detail_took_longer') }}
              </p>
              <div class="grid grid-cols-2 gap-x-6">
                <BaseAtomsButton size="sm" class="capitalize font-bold bg-transparent hover:bg-transparent text-indigo-500 shadow-none" @click="selectedMode = 'ori'">
                  {{ $t('global_no') }}
                </BaseAtomsButton>
                <BaseAtomsButton size="sm" class="capitalize font-bold" @click="{ repeatedAction++; startTimer() }">
                  {{ $t('global_yes') }}
                </BaseAtomsButton>
              </div>
            </div>

            <!-- SHOW MODAL: ERROR  -->
            <div v-if="isError" class="my-20 p-6 shadow-md rounded-sm w-[368px] text-center space-y-6 text-gray-900 m-auto">
              <Icon name="mdi:alert" class="w-12 h-12 text-yellow-500" />
              <p class="text-2xl font-bold">
                {{ $t('global_something_went_wrong') }}
              </p>
              <p class="text-sm">
                {{ $t('job_detail_unable_simplify') }} (error code: {{ isError }})
              </p>
              <div class="grid grid-cols-2 gap-x-6">
                <BaseAtomsButton size="sm" class="capitalize font-bold bg-transparent hover:bg-transparent text-indigo-500 shadow-none" @click="selectedMode = 'ori'">
                  {{ $t('global_cancel') }}
                </BaseAtomsButton>
                <BaseAtomsButton size="sm" class="capitalize font-bold" @click="useSimplified()">
                  {{ $t('global_try_again') }}
                </BaseAtomsButton>
              </div>
            </div>
          </div>

          <!-- JOB SUMMARY -->
          <div v-if="!isEmpty(jobSummary)" class="text-sm">
            <div class="flex items-center font-bold bg-blue-50 px-2 py-1 mb-2 rounded-md w-max">
              <Icon name="material-symbols:stream" class="w-4 h-4 mr-2 text-[#5843E5]" />
              Job Summary
            </div>

            <div v-if="!isEmpty(job?.ver === 2)">
              {{ job?.jobSummary }}
            </div>
            <div v-else v-html="jobSummary" />
          </div>

          <!-- JOB RESPONSIBILITY -->
          <div v-if="!isEmpty(jobResponsibility)" class="text-sm">
            <div v-if="selectedMode !== 'ori'" class="flex items-center font-bold bg-blue-50 px-2 py-1 mb-2 rounded-md w-max">
              <Icon name="material-symbols:engineering" class="w-4 h-4 mr-2 text-[#5843E5]" />
              Key Responsibilities
            </div>
            <ul v-if="!isEmpty(job?.ver === 2)" class="grid gap-y-1 list-disc !my-0 ml-6">
              <li v-for="(item, idx) in job?.keyResponsibilities" :key="idx" class="!m-0 !pl-1">
                {{ item }}
              </li>
            </ul>
            <div v-else v-html="jobResponsibility" />
          </div>

          <!-- JOB QUALIFICATION -->
          <div v-if="!isEmpty(jobRequirement)" class="text-sm">
            <div v-if="selectedMode !== 'ori'" class="flex items-center font-bold bg-blue-50 px-2 py-1 mb-2 rounded-md w-max">
              <Icon name="material-symbols:playlist-add-check" class="w-4 h-4 mr-2 text-[#5843E5]" />
              Qualifications and Requirements
            </div>
            <ul v-if="!isEmpty(job?.ver === 2)" class="grid gap-y-1 list-disc !my-0 ml-6">
              <li v-for="(item, idx) in job?.qualifications" :key="idx" class="!m-0 !pl-1">
                {{ item }}
              </li>
            </ul>
            <div v-else v-html="jobRequirement" />
          </div>

          <!-- JOB BENEFITS -->
          <div v-if="!isEmpty(jobBenefit)" class="text-sm">
            <div v-if="selectedMode !== 'ori'" class="flex items-center font-bold bg-blue-50 px-2 py-1 mb-2 rounded-md w-max">
              <Icon name="material-symbols:trending-up" class="w-4 h-4 mr-2 text-[#5843E5]" />
              Compensation and Benefits
            </div>
            <ul v-if="!isEmpty(job?.ver === 2)" class="grid gap-y-1 list-disc !my-0 ml-6">
              <li v-for="(item, idx) in job?.compensation" :key="idx" class="!m-0 !pl-1">
                {{ item }}
              </li>
            </ul>
            <div v-else v-html="jobBenefit" />
          </div>

          <!-- FAQ -->
          <div v-if="!isEmpty(job?.faq)" class="text-sm">
            <div class="flex items-center font-bold bg-blue-50 px-2 py-1 mb-2 rounded-md w-max">
              <Icon name="material-symbols:quiz-outline" class="w-4 h-4 mr-2 text-[#5843E5]" />
              FAQ
            </div>
            <table class="text-left ml-2 !text-sm grid">
              <template v-for="(item, idx) in job?.faq" :key="idx">
                <tr>
                  <th width="24px" class="align-top">
                    Q:
                  </th>
                  <th>{{ item.question }}</th>
                </tr>
                <tr class="mb-4">
                  <td width="24px" />
                  <td class="text-gray-600">
                    {{ item.answer }}
                  </td>
                </tr>
              </template>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- RIGHT SIDE -->
    <div v-if="rightShowing" class="col-span-12 lg:col-span-4 grid gap-y-6 auto-rows-min">
      <!-- COMPANY CARD -->
      <JobsCompanyCard :item="job" />

      <!-- SIMILAR JOBS -->
      <JobsSimilarJob :item="job" />
    </div>
  </div>
</template>
